/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React, { useCallback, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import type { VisibilityImpact } from '../../types/artifact-usage';

/**
 * Structurally identical to the local aliases in `ObjectMetadataPanel` and `ObjectMetadataBadges`,
 * which each declare their own. Not a fourth copy so much as the one the shared gesture needs; the
 * three collapse when those components stop declaring theirs.
 */
export type ConfigObjectVisibility = 'public' | 'private';

/**
 * **Say what a visibility change discloses, before it happens** (`#1124`, `P3a`).
 *
 * ## Why a hook and not a prop on each badge
 *
 * Two shared components render a visibility toggle — `ObjectMetadataPanel` and
 * `ObjectMetadataBadges` — with their own copies of the click handler and their own tooltip
 * strings. Adding the statement to both would be *shared logic is not shared words* (`#1187`)
 * arriving by the front door: a duplicated **explanation** produces a different sentence, which
 * nothing checks, read at the moment an operator is most likely to believe it.
 *
 * So the gesture lives here once — the fetch, the decision about whether there is anything to say,
 * the sentences, and the dialog — and each component renders `dialog` and calls `request`.
 *
 * ## Why it states rather than prevents
 *
 * `#1124`'s first proposed fix was a clamp (*a child may not be more visible than its parent*) and
 * Todd's review killed it: it forbids a private baseline with published variants, it infers a
 * disclosure intent `visibility` does not carry, and the containment it protects never existed —
 * *"viewing is enough to clone."* The defect is that **nobody decides that the disclosure happens**.
 *
 * ## Silent when there is nothing to say
 *
 * An artifact with no parent and no public children changes visibility with no dialog at all. A
 * confirm on every toggle is how an operator learns to click through the one that matters — the
 * same reasoning that keeps the boot-recovery broadcast quiet on a healthy start.
 */

/** Asks the server what a change to `to` would disclose. `null` where the surface cannot say. */
export type VisibilityImpactReader = ((to: ConfigObjectVisibility) => Promise<VisibilityImpact>) | null;

interface PendingChange {
  to: ConfigObjectVisibility;
  impact: VisibilityImpact;
}

/**
 * Turn an impact into the sentence an operator reads.
 *
 * **Names the parent and counts the values; never lists them.** *"4 values"* is actionable and
 * *"endpoint, headers.Authorization, timeout, protocols"* is a disclosure with a shrug attached —
 * the paths are carried so the sentence can name a *kind* (`including a request header`), not so a
 * surface can print the lot.
 */
export function describeImpact(impact: VisibilityImpact): { title: string; body: string } | null {
  if (impact.kind === 'widening' && impact.inherited) {
    const { parentName, valueCount } = impact.inherited;
    const values = valueCount === 1 ? '1 value' : `${valueCount} values`;
    const header = impact.inherited.notablePaths.some((p) => p.toLowerCase().startsWith('headers.'))
      ? ', including a request header'
      : '';
    return {
      title: 'Make this public?',
      body:
        `This also publishes ${values} inherited from ${parentName}${header}. ` +
        'They are part of what this artifact resolves to, so anyone who can see it can see them.',
    };
  }

  if (impact.kind === 'narrowing' && (impact.publicChildren || impact.othersChildren)) {
    const sentences: string[] = [];
    if (impact.publicChildren) {
      const { count, names } = impact.publicChildren;
      const subject = count === 1 ? '1 artifact that extends' : `${count} artifacts that extend`;
      sentences.push(
        `${subject} this one ${count === 1 ? 'is' : 'are'} public — ${names.join(', ')}. ` +
          'Their copies of these values stay visible; making this private does not retract them.',
      );
    }
    if (impact.othersChildren) {
      sentences.push(describeOthersChildren(impact.othersChildren));
    }
    return { title: 'Make this private?', body: sentences.join(' ') };
  }

  // Nothing inherited and no public children: the change discloses nothing beyond itself.
  return null;
}

/**
 * **Children someone else manages keep working, and their owners lose sight of the parent** (`RQ11h`,
 * ruled by Todd 2026-10-05). The outcome for each party, not the mechanism: nothing is detached, and
 * nobody but the people named will notice until they open what they own. A child this operator may not
 * see is counted, never named.
 */
function describeOthersChildren({
  count,
  names,
  hiddenCount,
}: NonNullable<VisibilityImpact['othersChildren']>): string {
  const one = count === 1;
  const listed = names.length > 0 ? ` — ${names.join(', ')}` : '';
  const hidden =
    hiddenCount > 0 ? ` (${hiddenCount === count ? (one ? 'one' : 'all') : hiddenCount} you cannot see)` : '';
  return (
    `${one ? '1 artifact' : `${count} artifacts`} managed by someone else ${one ? 'extends' : 'extend'} this one${listed}${hidden}. ` +
    `${one ? 'It keeps' : 'They keep'} inheriting from it, but ${one ? 'its owner' : 'their owners'} will no longer see what ${
      one ? 'it inherits' : 'they inherit'
    }.`
  );
}

export function useVisibilityChange(
  readImpact: VisibilityImpactReader,
  onChange: ((visibility: ConfigObjectVisibility) => void) | undefined,
): { request: (to: ConfigObjectVisibility) => void; dialog: React.ReactNode } {
  const [pending, setPending] = useState<PendingChange | null>(null);

  const request = useCallback(
    (to: ConfigObjectVisibility) => {
      if (!onChange) return;
      if (!readImpact) {
        onChange(to);
        return;
      }
      void readImpact(to)
        .then((impact) => {
          if (describeImpact(impact)) setPending({ to, impact });
          else onChange(to);
        })
        // A failed read must not block the operator's own change: the statement is an addition, and
        // withholding the gesture because we could not describe it would make the feature a gate.
        // It also must not silently claim there was nothing to say — hence the log.
        .catch((err) => {
          console.warn('Could not describe what this visibility change discloses', err);
          onChange(to);
        });
    },
    [onChange, readImpact],
  );

  const described = pending ? describeImpact(pending.impact) : null;

  return {
    request,
    dialog:
      pending && described ? (
        <ConfirmDialog
          title={described.title}
          body={described.body}
          confirmLabel={pending.to === 'public' ? 'Make public' : 'Make private'}
          tone="neutral"
          onConfirm={() => {
            const { to } = pending;
            setPending(null);
            onChange?.(to);
          }}
          onCancel={() => setPending(null)}
        />
      ) : null,
  };
}
